import React from 'react'
import { Comment as WishComment, WishItem } from '../utils/api'

export interface WishItemCardProps {
  item: WishItem
  isOwnList: boolean
  canSeePurchaseStatus: boolean
  currentUserId?: string | null
  ownerDisplayName: string
  isDeleting: boolean
  isToggling: boolean
  onTogglePurchased: (itemId: string, currentState: boolean, purchaserId?: string | null) => void
  onDeleteItem: (itemId: string) => void
  resolveDisplayName: (identifier?: string | null) => string
  formatCreatedTime: (timestamp?: number | null) => string | null
  comments: WishComment[]
  commentsLoading: boolean
  commentsError?: string | null
  commentInput: string
  commentError?: string | null
  onCommentInputChange: (itemId: string, value: string) => void
  commentVisibleToOwner: boolean
  onCommentVisibilityChange: (itemId: string, visible: boolean) => void
  onSubmitComment: (itemId: string) => void | Promise<void>
  isCommentSubmitting: boolean
}

const WishItemCard: React.FC<WishItemCardProps> = ({
  item,
  isOwnList,
  canSeePurchaseStatus,
  currentUserId,
  ownerDisplayName,
  isDeleting,
  isToggling,
  onTogglePurchased,
  onDeleteItem,
  resolveDisplayName,
  formatCreatedTime,
  comments,
  commentsLoading,
  commentsError,
  commentInput,
  commentError,
  onCommentInputChange,
  commentVisibleToOwner,
  onCommentVisibilityChange,
  onSubmitComment,
  isCommentSubmitting
}) => {
  const isMarkedPurchased = canSeePurchaseStatus && item.purchased
  const isToggleDisabled = isToggling || (item.purchased && (!item.purchasedBy || item.purchasedBy !== currentUserId))
  const createdLabel = formatCreatedTime(item.createdAt)
  const isSuggestion = Boolean(item.isSuggestion)
  const attributionName = isSuggestion ? resolveDisplayName(item.createdBy) : ownerDisplayName
  const attributionPrefix = isSuggestion ? 'suggested' : 'wished for'
  const attributionText = `${attributionName} ${attributionPrefix} this${createdLabel ? ` ${createdLabel}` : ''}`
  const safeComments = comments ?? []
  const [isCommentFormOpen, setIsCommentFormOpen] = React.useState(false)
  const commentFormId = `comment-form-${item.id}`

  const handleCommentSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onSubmitComment(item.id)
  }

  const handleCommentInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onCommentInputChange(item.id, event.target.value)
  }

  const handleCommentVisibilityToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    onCommentVisibilityChange(item.id, event.target.checked)
  }

  return (
    <div
      className="card"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        opacity: isMarkedPurchased ? 0.6 : 1
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>
            {item.url ? (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--primary-color)', textDecoration: 'none' }}
              >
                {item.name}
              </a>
            ) : (
              item.name
            )}
          </h3>
          {isSuggestion && (
            <span
              style={{
                backgroundColor: '#fff2e0',
                color: '#a34f0b',
                borderRadius: '999px',
                fontSize: '0.75rem',
                fontWeight: 600,
                padding: '0.1rem 0.6rem'
              }}
            >
              Suggestion
            </span>
          )}
        </div>
        {item.description && (
          <p style={{ color: '#666', margin: '0.5rem 0' }}>
            {item.description}
          </p>
        )}
        {item.imageUrl && (
          <div style={{ margin: '0.35rem 0' }}>
            <img
              src={item.imageUrl}
              alt={`${item.name} preview image`}
              style={{ maxWidth: '160px', borderRadius: '8px', border: '1px solid #eee', display: 'block' }}
              loading="lazy"
            />
          </div>
        )}
        {(createdLabel || isSuggestion) && (
          <p style={{ color: '#888', fontSize: '0.85rem', margin: 0 }}>
            {attributionText}
          </p>
        )}
        <div style={{ marginTop: '0.75rem', borderTop: '1px solid #eee', paddingTop: '0.75rem' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.5rem'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#333' }}>
                Comments {safeComments.length > 0 ? `(${safeComments.length})` : ''}
              </h4>
              <button
                type="button"
                onClick={() => setIsCommentFormOpen((prev) => !prev)}
                aria-expanded={isCommentFormOpen}
                aria-controls={commentFormId}
                style={{
                  border: '1px solid #ccc',
                  borderRadius: '50%',
                  width: '1.6rem',
                  height: '1.6rem',
                  lineHeight: '1.6rem',
                  textAlign: 'center',
                  background: '#fff',
                  cursor: 'pointer'
                }}
              >
                {isCommentFormOpen ? '-' : '+'}
              </button>
            </div>
            {commentsLoading && <span style={{ fontSize: '0.8rem', color: '#888' }}>Loading...</span>}
          </div>
          {!commentsLoading && commentsError && (
            <p style={{ color: '#c00', margin: '0 0 0.5rem 0' }}>{commentsError}</p>
          )}
          {!commentsLoading && !commentsError && safeComments.length === 0 && (
            <p style={{ color: '#777', margin: '0 0 0.5rem 0' }}>No comments yet.</p>
          )}
          {!commentsLoading && safeComments.length > 0 && (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.5rem' }}>
              {safeComments.map((comment) => {
                const commenterName = resolveDisplayName(comment.commenterId)
                const commentTime = formatCreatedTime(comment.createdAt)
                const isVisibleToOwner = comment.visibleToOwner !== false
                return (
                  <li
                    key={comment.id}
                    style={{
                      background: '#f9fafb',
                      borderRadius: '8px',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #eee'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: '0.75rem'
                      }}
                    >
                      <p style={{ margin: 0, fontSize: '0.85rem', color: '#444', whiteSpace: 'pre-wrap', flex: 1 }}>
                        <strong style={{ color: '#333', marginRight: '0.35rem' }}>
                          {commenterName}:
                        </strong>
                        {comment.text}
                      </p>
                      {commentTime && (
                        <span style={{ fontSize: '0.75rem', color: '#999', whiteSpace: 'nowrap' }}>
                          {commentTime}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.35rem', fontSize: '0.75rem' }}>
                      <span style={{ color: isVisibleToOwner ? '#2f855a' : '#a34f0b', fontWeight: 600 }}>
                        {isVisibleToOwner ? `Visible to ${ownerDisplayName}` : `Hidden from ${ownerDisplayName}`}
                      </span>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
          {isCommentFormOpen && (
            <form
              id={commentFormId}
              onSubmit={handleCommentSubmit}
              style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}
            >
              <textarea
                value={commentInput}
                onChange={handleCommentInputChange}
                rows={2}
                placeholder="Share a quick note or thought"
                disabled={isCommentSubmitting}
                style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ccc', resize: 'vertical' }}
              />
              {!isOwnList && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#444' }}>
                  <input
                    type="checkbox"
                    checked={commentVisibleToOwner}
                    onChange={handleCommentVisibilityToggle}
                    disabled={isCommentSubmitting}
                  />
                  Visible to {ownerDisplayName}
                </label>
              )}
              {commentError && (
                <p style={{ color: '#c00', margin: 0 }}>{commentError}</p>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="submit"
                  className="btn btn-secondary"
                  disabled={isCommentSubmitting || !commentInput.trim()}
                >
                  {isCommentSubmitting ? 'Posting...' : 'Post Comment'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
        {!isOwnList && (
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => onTogglePurchased(item.id, item.purchased, item.purchasedBy)}
            disabled={isToggleDisabled}
            style={{
              backgroundColor: isMarkedPurchased ? '#28a745' : 'var(--secondary-color)',
              fontSize: '0.875rem',
              cursor: isToggleDisabled ? 'default' : 'pointer'
            }}
          >
            {isToggling
              ? 'Updating...'
              : isMarkedPurchased
                ? item.purchasedBy === currentUserId ? 'Unacquire' : 'Acquired'
                : 'Mark as Acquired'}
          </button>
        )}
        {isOwnList && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onDeleteItem(item.id)}
            disabled={isDeleting}
            style={{ backgroundColor: '#c53030', fontSize: '0.875rem' }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        )}
      </div>
    </div>
  )
}

export default WishItemCard
